import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingButton } from "@mui/lab";
import BasicDialog from "../../components/base/BasicDialog";
import { FilterTableWrapper } from "../../components/table/BetterFilterTable/BetterFilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import usePaginationWithLocalSearchParamsAndOrdering from "../../hooks/usePaginationWithLocalSearchParamsAndOrdering";
import {
    useInvestmentSelectList,
} from "../../services/productsService";
import { AgreementPassportButton } from "../AgreementsPage/AgreementPassportDialog/AgreementPassportButton";
import { useShortDataWithBankingAgreements } from "../../services/agreementsService";
import { TEXT_FIELD_TYPE, SELECT_FIELD_TYPE } from "../../helpers/constants";
import {Checkbox, Typography} from "@mui/material";
import toast from "react-hot-toast";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { useToasts } from "../../hooks/useToasts";
import { useUpdateTransferIndividualBillMutation } from "../../services/bankingService";
import { useForm } from "react-hook-form"; 

export function MatchTransferWithAgreementDialog(props) {
    const { t } = useTranslation();

    const [agreementsDataLocal, setAgreementsDataLocal] = useState([]);
    const [agreementsCountLocal, setAgreementsCountLocal] = useState(0);

    const pagination = usePaginationWithLocalSearchParamsAndOrdering();

    const agreeemntSearchParams = useMemo(() => {
        const params = new URLSearchParams(pagination.searchParams);
        params.set("page", String(pagination.page));
        params.set("page_size", String(pagination.pageSize));
        params.set("exclude_reservation_agreements", true)
        if (props.isWithdrawal) {
            params.set("investment_is_developer", true)
        }
        return params;
    }, [pagination.searchParams, pagination.page, pagination.pageSize]);

    const { data: agreementsData } = useShortDataWithBankingAgreements(agreeemntSearchParams);
    const {data: actualAgreementData} = useShortDataWithBankingAgreements(new URLSearchParams({agreement_id: props.agreementId}), {enabled:props.agreementId !== null});
    useEffect(() => {
        if (agreementsData?.results) {
            setAgreementsDataLocal(agreementsData?.results);
            setAgreementsCountLocal(agreementsData.count);
        }
    }, [agreementsData]);

    const { data: investments, isLoading: isInvestmentLoading } = useInvestmentSelectList(
        props.isWithdrawal ? { investment_is_developer: true } : {},
        {
        select: (data) =>
            data.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
        })),
    });

    
    const [selectedBankingId, setSelectedBankingId] = useState()
    const handleSelectAgreementId = (rowData) => {
        const bankingId = props.isWithdrawal ? rowData.banking_id_withdrawal || rowData.banking_id : rowData.banking_id
        setSelectedBankingId(bankingId)
    };

    const handleUnselectAgreementId = () => {
        setSelectedBankingId(null)
    };

    const { onFormErrors, onServerError } = useToasts();
    const { mutate: updateTransfer, isPending: isUpdatingTransfer } = useUpdateTransferIndividualBillMutation({
            onSuccess: () => {
                toast.custom(
                    () => <SnackbarSuccess message={t("successfullyMerged")} />,
                    {
                        position: "bottom-right",
                    },
                );
                props.onClose()
            },
            onError: (error) => {
                onServerError(error);
            },
        });

    const { handleSubmit } = useForm({defaultValues: {}});

    const onSubmit = (data) => {
        updateTransfer({data:{transfer_banking: selectedBankingId}, id:props.transferId})
    }; 

    
    const getTableFieldsConfig = (excludeFields=[]) => {
        let tableConfig = [
            {
                name: "checkboox",
                filterType: null,
                label: t("select"),
                getValueCallback: (row) => (
                    <Checkbox
                        checked={selectedBankingId === (row.banking_id_withdrawal || row.banking_id)}
                        onChange={(e) => {
                            if (e.currentTarget.checked) {
                                handleSelectAgreementId(row);
                            } else {
                                handleUnselectAgreementId();
                            }
                        }}
                    />
                ),
            },
            {
                name: "agreement_nr",
                getValueCallback: (rowData) => rowData.agreement_nr,
                label: t("table.agreements.agreementNr"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: true,
            },
            {
                name: "agreement_notarial_deed",
                getValueCallback: (rowData) => rowData.agreement_notarial_deed || "",
                label: t("table.agreements.agreementNotarialDeed"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: true,
            },
            {
                name: "customer_full_name",
                getValueCallback: (rowData) => rowData.agreement_customer_person, 
                label: t("table.agreements.customerFullNames"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: false,
            },
            {
                name: "counterparty_names",
                getValueCallback: (rowData) => rowData.agreement_customer_company,
                label: t("table.agreements.companyNames"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: false,
            },
            {
                name: "investment",
                getValueCallback: (rowData) => rowData.agreement_invest.investment_name_sales,
                label: t("table.agreements.investment"),
                filterType: SELECT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => ({
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                    multiple: false,
                }),
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                },
                sortable: false,
            },
            {
                name: "product_numbers",
                getValueCallback: (rowData) => rowData.agreement_local,
                label: t("table.agreements.productNumbers"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: false,
            },
            {
                name: "banking_nr",
                getValueCallback: (rowData) => rowData.banking_nr,
                label: t("bankings.banking_nr"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: false,
            },
            {
                name: "banking_nr_withdrawal",
                getValueCallback: (rowData) => rowData.banking_nr_withdrawal,
                label: t("bankings.banking_nr_withdrawal"),
                filterType: TEXT_FIELD_TYPE,
                getFilterFieldConfigCallback: () => undefined,
                sortable: false,
            },
            
            // {
            //     name: "passport",
            //     getValueCallback: (rowData) => <AgreementPassportButton agreement={{id:rowData.id}} />,
            //     label: t("table.agreements.passport"),
            //     getFilterFieldConfigCallback: () => undefined,
            //     sortable: false,
            // },            
        ]

        return tableConfig.filter(config=> !excludeFields.includes(config.name))
    }

    const ActualAgreementTable = () => {
           return( 
            <>
                <Typography color={"primary"} variant="h6" textAlign={"left"} marginTop={3}>{t("bankings.actualAgreement")}</Typography>
                <LoaderWrapper showLoader={isInvestmentLoading || !actualAgreementData}>
                    <FilterTableWrapper
                        data={actualAgreementData?.results}
                        countRecords={1}
                        filtersOpts={{hideFiltersIconCol: true}}
                        fieldsConfig={getTableFieldsConfig(["checkboox"])}
                    />
                </LoaderWrapper>
            </>
        )
    }

    const MergeTransferWithAgreementTable = () => {
        return( 
            <>
                <Typography color={"primary"} variant="h6" textAlign={"left"} marginTop={3}>{t("bankings.selectAgreementToMerge")}</Typography>
                <LoaderWrapper showLoader={isInvestmentLoading || !agreementsDataLocal}>
                    <FilterTableWrapper
                        data={agreementsDataLocal}
                        countRecords={agreementsCountLocal}
                        localPagination={pagination}
                        filtersOpts={{
                            showCleanFilterIcon: true,
                        }}
                        tableStyle={{
                            maxHeight: "80vh",
                            overflow: "auto",
                        }}
                        onSearchFiltersChange={pagination.handleChangeSearchParams}
                        fieldsConfig={getTableFieldsConfig()}
                    />
                </LoaderWrapper>
            </>
        )
    }

    return (
        <BasicDialog
            open={true}
            onClose={() => props.onClose()}
            titleAlign={"center"}
            contentAlign={"center"}
            title={props.isWithdrawal ? t("bankings.mergeTransferWithdrawalWithAgreement") : t("bankings.mergeTransferWithAgreement")}
            maxWidth={"xl"}
            showDialogActions
            alwaysFullScreen={false}
            contentSx={{ pt: 0 }}
        >
            {props.agreementId && ActualAgreementTable()}
            {MergeTransferWithAgreementTable()}
            <LoadingButton
                loading={isUpdatingTransfer}
                disabled={!selectedBankingId}
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleSubmit(onSubmit, onFormErrors)}
                fullWidth
            >
                {t("merge")}
            </LoadingButton>
        </BasicDialog>
    );
}
